import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fieldAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { 
  Search, 
  MapPin, 
  Star, 
  ChevronRight,
  Navigation,
  List,
  Map as MapIcon
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const createFieldIcon = () => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: #00B5A7; width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 4px 6px rgba(0,0,0,0.2);">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

// Tunis center coordinates
const TUNIS_CENTER = [36.8065, 10.1815];

// Map control component
const MapController = ({ selectedField }) => {
  const map = useMap();
  
  useEffect(() => {
    if (selectedField) {
      map.flyTo([selectedField.latitude, selectedField.longitude], 15, {
        duration: 1
      });
    }
  }, [selectedField, map]);
  
  return null;
};

export default function MapView() {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedField, setSelectedField] = useState(null);
  const [viewMode, setViewMode] = useState('map'); // 'map' or 'list'

  const fieldIcon = useMemo(() => createFieldIcon(), []);

  useEffect(() => {
    const fetchFields = async () => {
      try {
        const response = await fieldAPI.getAll();
        // Only show registered fields from the platform
        const apiFields = response.data || [];
        // Filter only active fields with valid coordinates
        const validFields = apiFields.filter(field => 
          field.is_active && 
          field.latitude && 
          field.longitude &&
          field.latitude !== 0 &&
          field.longitude !== 0
        );
        setFields(validFields);
      } catch (error) {
        console.error('Failed to fetch fields:', error);
        setFields([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFields();
  }, []);

  const filteredFields = fields.filter(field =>
    field.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    field.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
          <Skeleton className="w-full h-full" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="h-[calc(100vh-4rem)] flex flex-col md:flex-row" data-testid="map-view">
        {/* Sidebar */}
        <div className={`${viewMode === 'list' ? 'flex-1' : 'w-full md:w-96'} bg-white border-r border-slate-200 flex flex-col`}>
          {/* Search Header */}
          <div className="p-4 border-b border-slate-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder="Feld suchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12"
                data-testid="field-search"
              />
            </div>
            <div className="flex gap-2 mt-3 md:hidden">
              <Button
                variant={viewMode === 'map' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('map')}
                className="flex-1"
              >
                <MapIcon className="w-4 h-4 mr-2" />
                Karte
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="flex-1"
              >
                <List className="w-4 h-4 mr-2" />
                Liste
              </Button>
            </div>
          </div>

          {/* Field List */}
          <div className={`flex-1 overflow-auto p-4 space-y-3 ${viewMode === 'map' ? 'hidden md:block' : ''}`}>
            <p className="text-sm text-muted-foreground mb-2">
              {filteredFields.length} Felder gefunden
            </p>
            {filteredFields.length === 0 ? (
              <div className="text-center py-12">
                <MapPin className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="font-semibold text-slate-600 mb-2">Keine Felder registriert</h3>
                <p className="text-sm text-muted-foreground">
                  Es sind noch keine Fußballplätze auf der Plattform verfügbar.
                </p>
              </div>
            ) : (
              filteredFields.map((field) => (
              <Card
                key={field.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  selectedField?.id === field.id ? 'ring-2 ring-tunis-teal' : ''
                }`}
                onClick={() => setSelectedField(field)}
                data-testid={`field-card-${field.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                      {field.photos?.[0] ? (
                        <img
                          src={field.photos[0]}
                          alt={field.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-tunis-teal/20 to-tunis-blue/20">
                          <MapPin className="w-8 h-8 text-tunis-teal/50" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-tunis-blue truncate">{field.name}</h3>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{field.location}</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm mt-1">
                        <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                        <span className="font-medium">{field.rating || 0}</span>
                        <span className="text-muted-foreground">({field.total_reviews || 0})</span>
                      </div>
                      <div className="mt-2 font-bold text-tunis-teal">
                        {formatCurrency(field.price_per_hour)}/Std.
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
            )}
          </div>
        </div>

        {/* Map Container */}
        <div className={`flex-1 relative ${viewMode === 'list' ? 'hidden md:block' : ''}`}>
          <MapContainer
            center={TUNIS_CENTER}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            />
            <MapController selectedField={selectedField} />
            
            {filteredFields.map((field) => (
              <Marker
                key={field.id}
                position={[field.latitude, field.longitude]}
                icon={fieldIcon}
                eventHandlers={{
                  click: () => setSelectedField(field),
                }}
              >
                <Popup>
                  <div className="p-2 min-w-[220px]">
                    {field.photos?.[0] && (
                      <div className="w-full h-24 rounded-lg overflow-hidden mb-2">
                        <img
                          src={field.photos[0]}
                          alt={field.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <h3 className="font-bold text-tunis-blue">{field.name}</h3>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                      <MapPin className="w-3 h-3" />
                      {field.location?.substring(0, 40)}{field.location?.length > 40 ? '...' : ''}
                    </div>
                    <div className="flex items-center gap-1 text-sm mt-1">
                      <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                      <span className="font-medium">{field.rating || 0}</span>
                      <span className="text-muted-foreground">({field.total_reviews || 0})</span>
                    </div>
                    <div className="mt-2 font-bold text-tunis-teal">
                      {formatCurrency(field.price_per_hour)}/Std.
                    </div>
                    <Link to={`/fields/${field.id}`}>
                      <Button size="sm" className="w-full mt-3 btn-primary">
                        Details
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          {/* Selected Field Card (Mobile) */}
          {selectedField && viewMode === 'map' && (
            <div className="absolute bottom-4 left-4 right-4 md:hidden animate-slide-up">
              <Card className="shadow-floating">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                      {selectedField.photos?.[0] ? (
                        <img
                          src={selectedField.photos[0]}
                          alt={selectedField.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-tunis-teal/20 to-tunis-blue/20">
                          <MapPin className="w-10 h-10 text-tunis-teal/50" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-tunis-blue">{selectedField.name}</h3>
                      <div className="flex items-center gap-1 text-sm mt-1">
                        <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                        <span>{selectedField.rating || 0}</span>
                      </div>
                      <div className="font-bold text-tunis-teal mt-1">
                        {formatCurrency(selectedField.price_per_hour)}/Std.
                      </div>
                      <Link to={`/fields/${selectedField.id}`}>
                        <Button size="sm" className="mt-2 btn-primary">
                          Details
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Center on User Button */}
          <Button
            size="icon"
            className="absolute top-4 right-4 bg-white shadow-lg hover:bg-slate-50"
            onClick={() => {
              if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition((pos) => {
                  setSelectedField({
                    latitude: pos.coords.latitude,
                    longitude: pos.coords.longitude,
                  });
                });
              }
            }}
            data-testid="center-location-btn"
          >
            <Navigation className="w-5 h-5 text-tunis-blue" />
          </Button>
        </div>
      </div>
    </Layout>
  );
}
